import {Modal, FormCheck, Form} from 'react-bootstrap'
import React from 'react'
import './FlowSettingsModal.scss'
import {FlowBusinessHoursSetting} from '../WorkFlow/FlowTypes'
export interface FlowSettingItem {
  feature: string
  value: boolean
  desc: string
  learnMore?: string
  onToggle: (checked: boolean) => void
}

interface FlowSettingsModalProps {
  show: boolean
  onHide: () => void
  onSave: () => void
  flowName?: string
  settings: FlowSettingItem[]
  saving?: boolean
  businessHours?: FlowBusinessHoursSetting
  onBusinessHoursChange?: (next: FlowBusinessHoursSetting) => void
}

const FlowSettingsModal: React.FC<FlowSettingsModalProps> = ({
  show,
  onHide,
  onSave,
  flowName,
  settings,
  saving = false,
  businessHours,
  onBusinessHoursChange,
}) => {
  const handleToggleDay = (day: number) => {
    if (!businessHours || !onBusinessHoursChange) return
    const exists = businessHours.days.includes(day)
    const nextDays = exists
      ? businessHours.days.filter((d) => d !== day)
      : [...businessHours.days, day].sort()
    onBusinessHoursChange({...businessHours, days: nextDays})
  }

  const handleTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    if (!businessHours || !onBusinessHoursChange) return
    onBusinessHoursChange({...businessHours, [field]: value})
  }

  const handleTimezoneChange = (value: string) => {
    if (!businessHours || !onBusinessHoursChange) return
    onBusinessHoursChange({...businessHours, timezone: value})
  }

  const dayLabels: {value: number; label: string}[] = [
    {value: 1, label: 'Mon'},
    {value: 2, label: 'Tue'},
    {value: 3, label: 'Wed'},
    {value: 4, label: 'Thu'},
    {value: 5, label: 'Fri'},
    {value: 6, label: 'Sat'},
    {value: 0, label: 'Sun'},
  ]

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName='flow-settings-modal'>
      <Modal.Header closeButton className='flow-settings-modal__header'>
        <div className='flow-settings-modal__header-title'>
          <span>Settings</span>
          <span>{flowName ? `- ${flowName}` : ''}</span>
        </div>
      </Modal.Header>
      <Modal.Body>
        {settings.map((item, idx) => (
          <div key={item.feature + idx} className='mb-3'>
            <FormCheck
              id={`flow-setting-${idx}`}
              type='checkbox'
              label={item.feature}
              checked={item.value}
              onChange={(e) => item.onToggle(e.target.checked)}
              className='flow-settings-checkbox'
            />
            <div className='mt-2 text-muted flow-settings-description'>
              {item.desc}{' '}
              {item.learnMore ? (
                <a href={item.learnMore} target='_blank' rel='noreferrer'>
                  Learn More
                </a>
              ) : null}
            </div>
          </div>
        ))}
        {businessHours && onBusinessHoursChange ? (
          <div className='mt-4'>
            <h6 className='fw-semibold mb-2'>Business hours</h6>
            <p className='text-muted mb-3'>
              Only process flow steps during the selected days and time window (based on the chosen
              timezone).
            </p>

            <div className='mb-3'>
              <div className='mb-1 fw-semibold'>Days of week</div>
              <div className='d-flex flex-wrap gap-2'>
                {dayLabels.map((d) => (
                  <button
                    key={d.value}
                    type='button'
                    className={`btn btn-sm ${
                      businessHours.days.includes(d.value) ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                    onClick={() => handleToggleDay(d.value)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className='mb-3 d-flex gap-3 align-items-end'>
              <Form.Group>
                <Form.Label className='fw-semibold'>Start time</Form.Label>
                <Form.Control
                  type='time'
                  value={businessHours.startTime}
                  onChange={(e) => handleTimeChange('startTime', e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className='fw-semibold'>End time</Form.Label>
                <Form.Control
                  type='time'
                  value={businessHours.endTime}
                  onChange={(e) => handleTimeChange('endTime', e.target.value)}
                />
              </Form.Group>
              <Form.Group className='flex-grow-1'>
                <Form.Label className='fw-semibold'>Timezone</Form.Label>
                <Form.Select
                  value={businessHours.timezone}
                  onChange={(e) => handleTimezoneChange(e.target.value)}
                >
                  <option value='Australia/Melbourne'>Australia/Melbourne</option>
                  <option value='Australia/Sydney'>Australia/Sydney</option>
                  <option value='UTC'>UTC</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <button className='btn btn-light' onClick={onHide} disabled={saving}>
          Close
        </button>
        <button className='btn btn-primary' onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default FlowSettingsModal


